import { useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { DatedVersionSelector, DatedVersion } from "@/components/shared/DatedVersionSelector";
import { ArrowLeft, Download, Share2, RefreshCw, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UnifiedLandscapeTable } from "@/components/keyword-landscape-output";
import { IncompleteScoreBadge } from "@/components/keyword-landscape-output/IncompleteScoreBadge";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { KeywordRanking, LandscapeTab, LandscapeOutputData } from "@/types";
import { toast } from "sonner";
import { buildLandscapeCsv, downloadBlob, landscapeCsvFilename } from "@/utils/landscapeCsvExport";

const TABS = [
  { value: LandscapeTab.Keyword, label: "Keyword Analysis" },
  { value: LandscapeTab.Competitor, label: "Competitor Analysis" },
];

interface LandscapeDetailViewProps {
  landscapeData: LandscapeOutputData;
  selectedInstanceId: string;
  allInstances: DatedVersion[];
  activeTab: LandscapeTab;
  productId: number;
  productIdParam?: string;
  isDetailFetching: boolean;
  shareModalOpen: boolean;
  readOnly: boolean;
  onActiveTabChange: (tab: LandscapeTab) => void;
  onInstanceChange: (id: string) => void;
  onBack: () => void;
  onShareModalOpenChange: (open: boolean) => void;
}

export function LandscapeDetailView({
  landscapeData,
  selectedInstanceId,
  allInstances,
  activeTab,
  productId,
  productIdParam,
  isDetailFetching,
  shareModalOpen,
  readOnly,
  onActiveTabChange,
  onInstanceChange,
  onBack,
  onShareModalOpenChange,
}: LandscapeDetailViewProps) {
  const filteredKeywordsRef = useRef<KeywordRanking[]>([]);

  const handleFilteredKeywordsChange = useCallback((keywords: KeywordRanking[]) => {
    filteredKeywordsRef.current = keywords;
  }, []);

  const handleExport = () => {
    const keywords =
      filteredKeywordsRef.current.length > 0
        ? filteredKeywordsRef.current
        : landscapeData.keywords;

    try {
      const csv = buildLandscapeCsv(landscapeData, keywords);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, landscapeCsvFilename(landscapeData));
      toast.success(`Exported ${keywords.length} keywords to CSV.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    }
  };

  return (
    <div className="space-y-4 flex flex-col flex-1 relative min-h-scree">
      {isDetailFetching && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading landscape data...</p>
          </div>
        </div>
      )}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-fit"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to all landscapes
      </button>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-semibold text-foreground capitalize">
            {landscapeData.prioritySearchPhrase}
          </h2>
          <IncompleteScoreBadge
            factorAvailability={landscapeData.factor_availability}
            pointsAvailable={landscapeData.points_available}
          />
        </div>
        <div className="flex items-center gap-2">
          <DatedVersionSelector
            versions={allInstances}
            value={selectedInstanceId}
            onValueChange={onInstanceChange}
            placeholder="Select date"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-popover border border-border"
              style={{ borderRadius: "8px" }}
            >
              <DropdownMenuItem onClick={handleExport} className="cursor-pointer">
                <Download className="mr-2 h-4 w-4" />
                Export
              </DropdownMenuItem>
              {!readOnly ? (
                <DropdownMenuItem
                  onClick={() => onShareModalOpenChange(true)}
                  className="cursor-pointer"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={(v) => onActiveTabChange(v as LandscapeTab)}>
          <TabsList variant="segmented">
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} variant="segmented" value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center border border-border rounded-sm divide-x divide-border">
          <div className="px-4 py-2 text-center">
            <p className="text-[10px] text-muted-foreground font-medium">Relevant Keywords</p>
            <p className="text-sm font-semibold text-foreground">
              {landscapeData.totals.relevantKeywords.toLocaleString()}
            </p>
          </div>
          <div className="px-4 py-2 text-center">
            <p className="text-[10px] text-muted-foreground font-medium">Exact Volume</p>
            <p className="text-sm font-semibold text-foreground">
              {landscapeData.totals.exactVolume.toLocaleString()}
            </p>
          </div>
          <div className="px-4 py-2 text-center">
            <p className="text-[10px] text-muted-foreground font-medium">Monthly Units</p>
            <p className="text-sm font-semibold text-foreground">
              {landscapeData.totals.monthlyUnits.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <UnifiedLandscapeTable
        data={landscapeData}
        productId={productId}
        onInstanceChange={onInstanceChange}
        activeTab={activeTab}
        onFilteredKeywordsChange={handleFilteredKeywordsChange}
      />

      <ShareLinkModal
        open={shareModalOpen}
        onOpenChange={onShareModalOpenChange}
        shareableType="landscape"
        shareableId={selectedInstanceId}
        resourceName={landscapeData.prioritySearchPhrase}
      />
    </div>
  );
}
